import React from "react";
import FieldLabel from "../../ui/FieldLabel";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/Collapsible";
import {
  AUTOLIMITS_OPTIONS,
  FlagSelect,
  NullableNumberInput,
  PanelNumberInput,
  Row,
  clampedDecimal,
} from "./PhysicsPanelControls";

export default function PhysicsAdvancedMujocoSettingsSection({
  isAuthoringLocked,
  mjcfCompiler,
  mjcfStatistic,
  sceneActions,
  sceneOption,
  showAdvancedMujocoSettings,
  setShowAdvancedMujocoSettings,
  updateSceneOption,
}) {
  return (
    <>
      <Collapsible open={showAdvancedMujocoSettings} onOpenChange={setShowAdvancedMujocoSettings}>
        <CollapsibleTrigger
          quiet
          className="w-full text-xs text-gray-200 justify-between"
          disabled={isAuthoringLocked}
        >
          Advanced MuJoCo Settings
        </CollapsibleTrigger>
        <CollapsibleContent>
          <FieldLabel as="div" className="px-3 pt-1 text-gray-300 select-none">
            Compiler
          </FieldLabel>
          <Row label="Autolimits">
            <FlagSelect
              value={mjcfCompiler.autolimits}
              onChange={(value) => sceneActions.updateMjcfCompiler({ autolimits: value })}
              options={AUTOLIMITS_OPTIONS}
              disabled={isAuthoringLocked}
              label="Autolimits"
            />
          </Row>
          <Row label="Strip Path">
            <FlagSelect
              value={mjcfCompiler.strippath}
              onChange={(value) => sceneActions.updateMjcfCompiler({ strippath: value })}
              disabled={isAuthoringLocked}
              label="Strip path"
            />
          </Row>
          <Row label="Use Thread">
            <FlagSelect
              value={mjcfCompiler.usethread}
              onChange={(value) => sceneActions.updateMjcfCompiler({ usethread: value })}
              disabled={isAuthoringLocked}
              label="Use thread"
            />
          </Row>
          <Row label="Bound Mass">
            <NullableNumberInput
              label="Bound Mass"
              value={mjcfCompiler.boundmass}
              min={0}
              disabled={isAuthoringLocked}
              onChange={(value) => sceneActions.updateMjcfCompiler({ boundmass: value })}
            />
          </Row>
          <Row label="Bound Inertia">
            <NullableNumberInput
              label="Bound Inertia"
              value={mjcfCompiler.boundinertia}
              min={0}
              disabled={isAuthoringLocked}
              onChange={(value) => sceneActions.updateMjcfCompiler({ boundinertia: value })}
            />
          </Row>
          <Row label="Total Mass">
            <NullableNumberInput
              label="Total Mass"
              value={mjcfCompiler.settotalmass}
              min={0}
              disabled={isAuthoringLocked}
              onChange={(value) => sceneActions.updateMjcfCompiler({ settotalmass: value })}
            />
          </Row>
          <Row label="Balance Inertia">
            <FlagSelect
              value={mjcfCompiler.balanceinertia}
              onChange={(value) => sceneActions.updateMjcfCompiler({ balanceinertia: value })}
              disabled={isAuthoringLocked}
              label="Balance inertia"
            />
          </Row>
          <Row label="Discard Visual">
            <FlagSelect
              value={mjcfCompiler.discardvisual}
              onChange={(value) => sceneActions.updateMjcfCompiler({ discardvisual: value })}
              disabled={isAuthoringLocked}
              label="Discard visual"
            />
          </Row>
          <Row label="Convex Hull">
            <FlagSelect
              value={mjcfCompiler.convexhull}
              onChange={(value) => sceneActions.updateMjcfCompiler({ convexhull: value })}
              disabled={isAuthoringLocked}
              label="Convex hull"
            />
          </Row>
          <Row label="Fuse Static">
            <FlagSelect
              value={mjcfCompiler.fusestatic}
              onChange={(value) => sceneActions.updateMjcfCompiler({ fusestatic: value })}
              disabled={isAuthoringLocked}
              label="Fuse static"
            />
          </Row>
          <Row label="Inertia From Geom">
            <FlagSelect
              value={mjcfCompiler.inertiafromgeom}
              onChange={(value) => sceneActions.updateMjcfCompiler({ inertiafromgeom: value })}
              disabled={isAuthoringLocked}
              label="Inertia from geom"
            />
          </Row>
          <Row label="Inertia Groups">
            <div className="grid w-full grid-cols-2 gap-1">
              {mjcfCompiler.inertiagrouprange.map((component, index) => (
                <NullableNumberInput
                  key={index}
                  label={`Inertia Group ${index + 1}`}
                  value={component}
                  min={0}
                  step={1}
                  className="w-full"
                  disabled={isAuthoringLocked}
                  onChange={(value) => {
                    const next = [...mjcfCompiler.inertiagrouprange];
                    next[index] = value === null ? null : Math.trunc(value);
                    sceneActions.updateMjcfCompiler({ inertiagrouprange: next });
                  }}
                />
              ))}
            </div>
          </Row>
          <Row label="Fit AABB">
            <FlagSelect
              value={mjcfCompiler.fitaabb}
              onChange={(value) => sceneActions.updateMjcfCompiler({ fitaabb: value })}
              disabled={isAuthoringLocked}
              label="Fit AABB"
            />
          </Row>
          <div className="flex justify-end px-3">
            <button
              type="button"
              className="px-2 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 text-xs disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isAuthoringLocked}
              onClick={() => sceneActions.resetMjcfCompiler()}
            >
              Reset Compiler
            </button>
          </div>
          <div className="h-px bg-gray-700 my-2" />
          <FieldLabel as="div" className="px-3 py-1 text-gray-300 select-none">
            Statistic
          </FieldLabel>
          <Row label="Mean Mass">
            <NullableNumberInput
              label="Mean Mass"
              value={mjcfStatistic.meanmass}
              min={0}
              disabled={isAuthoringLocked}
              onChange={(value) => sceneActions.updateMjcfStatistic({ meanmass: value })}
            />
          </Row>
          <Row label="Mean Inertia">
            <NullableNumberInput
              label="Mean Inertia"
              value={mjcfStatistic.meaninertia}
              min={0}
              disabled={isAuthoringLocked}
              onChange={(value) => sceneActions.updateMjcfStatistic({ meaninertia: value })}
            />
          </Row>
          <Row label="Mean Size">
            <NullableNumberInput
              label="Mean Size"
              value={mjcfStatistic.meansize}
              min={0}
              disabled={isAuthoringLocked}
              onChange={(value) => sceneActions.updateMjcfStatistic({ meansize: value })}
            />
          </Row>
          <Row label="Extent">
            <NullableNumberInput
              label="Extent"
              value={mjcfStatistic.extent}
              min={0}
              disabled={isAuthoringLocked}
              onChange={(value) => sceneActions.updateMjcfStatistic({ extent: value })}
            />
          </Row>
          <Row label="Center">
            <div className="grid w-full grid-cols-3 gap-1">
              {mjcfStatistic.center.map((component, index) => (
                <NullableNumberInput
                  key={index}
                  label={`Center ${["X", "Y", "Z"][index]}`}
                  value={component}
                  className="w-full"
                  disabled={isAuthoringLocked}
                  onChange={(value) => {
                    const next = [...mjcfStatistic.center];
                    next[index] = value;
                    sceneActions.updateMjcfStatistic({ center: next });
                  }}
                />
              ))}
            </div>
          </Row>
          <div className="flex justify-end px-3">
            <button
              type="button"
              className="px-2 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 text-xs disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isAuthoringLocked}
              onClick={() => sceneActions.resetMjcfStatistic()}
            >
              Reset Statistic
            </button>
          </div>
          <div className="h-px bg-gray-700 my-2" />
          <FieldLabel as="div" className="px-3 py-1 text-gray-300 select-none">
            Option
          </FieldLabel>
          <Row label="Noslip Iterations">
            <PanelNumberInput
              label="Noslip Iterations"
              step={1}
              min={0}
              max={2000}
              className="w-full"
              value={sceneOption.noslip_iterations}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                const raw = Number(e.target.value);
                const v = Math.max(0, Math.min(2000, Number.isFinite(raw) ? Math.floor(raw) : 0));
                updateSceneOption({ noslip_iterations: v });
              }}
            />
          </Row>
          <Row label="Noslip Tolerance">
            <PanelNumberInput
              label="Noslip Tolerance"
              step={1e-7}
              min={1e-12}
              max={1e-2}
              className="w-full"
              value={sceneOption.noslip_tolerance}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                updateSceneOption({
                  noslip_tolerance: clampedDecimal(e.target.value, 1e-6, 1e-12, 1e-2, 12),
                });
              }}
            />
          </Row>
          <Row label="CCD Iterations">
            <PanelNumberInput
              label="CCD Iterations"
              step={1}
              min={0}
              max={2000}
              className="w-full"
              value={sceneOption.mpr_iterations}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                const raw = Number(e.target.value);
                const v = Math.max(0, Math.min(2000, Number.isFinite(raw) ? Math.floor(raw) : 50));
                updateSceneOption({ mpr_iterations: v });
              }}
            />
          </Row>
          <Row label="CCD Tolerance">
            <PanelNumberInput
              label="CCD Tolerance"
              step={1e-7}
              min={1e-12}
              max={1e-2}
              className="w-full"
              value={sceneOption.mpr_tolerance}
              disabled={isAuthoringLocked}
              onChange={(e) => {
                updateSceneOption({
                  mpr_tolerance: clampedDecimal(e.target.value, 1e-6, 1e-12, 1e-2, 12),
                });
              }}
            />
          </Row>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
